import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Home.css";
import "./Carousel.css";
import Popup from "./popup";
import { API_BASE_URL } from "../config";
import Branch from "./Branch";
import Welcome from "./Welcome";
import CoreRpcCourses from "./CoreRpcCourses";
import AddOnCourses from "./AddOnCourses";
import Stats from "./Stats";
import WhyChooseGSAP from "./WhyChooseGSAP";
import ConsultationModal from "./ConsultationModal";
import CertificationOptimized from "./CertificationOptimized";

const Home = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const timeoutRef = useRef(null);
  const [testimonials, setTestimonials] = useState([]);
  const navigate = useNavigate();
  const [contactLoading, setContactLoading] = useState(false);
  const [, setCourses] = useState([]);
  const [showConsultModal, setShowConsultModal] = useState(false);

  // 🎯 Ref for courses section
  const coursesRef = useRef(null);

  // Contact form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    number: "",
    message: "",
  });
  const [status, setStatus] = useState("");

  // Collaborators state
  const [collaborators, setCollaborators] = useState([]);

  const nextSlide = () => setActiveIndex((prev) => (prev + 1) % slides.length);

  //Popup state
  const [showPopup, setShowPopup] = useState(true);

  const pickRandomThree = (arr) => {
    if (!Array.isArray(arr)) return [];
    const shuffled = [...arr].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  };

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      nextSlide();
    }, 5000);
    return () => clearTimeout(timeoutRef.current);
  }, [activeIndex]);

  useEffect(() => {
    const elements = document.querySelectorAll("[data-animate]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
          }
        });
      },
      { threshold: 0.3 },
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/courses`)
      .then((res) => res.json())
      .then((data) => setCourses(data))
      .catch((err) => console.error("Error fetching courses:", err));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/collaborators`)
      .then((res) => res.json())
      .then((data) => {
        console.log("COLLABORATORS DATA:", data);
        setCollaborators(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error("Collaborators fetch error:", err));
  }, []);

  // ================= CONTACT FORM =================
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (contactLoading) return;

    setContactLoading(true);
    setStatus("Sending...");

    try {
      const response = await fetch(`${API_BASE_URL}/home`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("✅ Message sent successfully!");
        setFormData({
          name: "",
          email: "",
          number: "",
          message: "",
        });
      } else {
        const err = await response.json();
        setStatus(err?.message || "❌ Failed to send message.");
      }
    } catch (error) {
      console.error(error);
      setStatus("⚠ Server error. Please try later.");
    } finally {
      setContactLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${API_BASE_URL}/api/testimonials`, {
      signal: controller.signal,
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => {
        const randomThree = pickRandomThree(data);
        setTestimonials(randomThree);
      })
      .catch(() => { });

    return () => controller.abort();
  }, []);
  console.log("Modal state:", showConsultModal);

  return (
    <div className="home-container">
      <Welcome />
      <Branch />
      <WhyChooseGSAP />
      <CertificationOptimized />

      {/* 🎯 COURSES SECTION - Popup handled inside CoreRpcCourses */}
      <div ref={coursesRef}>
        <CoreRpcCourses
          onConsultClick={() => {
            console.log("Opening modal...");
            setShowConsultModal(true);
          }}
        />
      </div>

      <AddOnCourses />

      {/* 
      ===== OUR PRODUCTS (ONLY DISPLAYED IN HOME) =====
      <div className="home-products">
        <OurProducts limit={3} />
      </div> 
      */}

      {/* ===== INFRASTRUCTURE ===== */}
      <section className="flytutor-infrastructure">
        <h2 className="flytutor-main-heading">Our Infrastructure</h2>
        <div className="infrastructure-bg-layer"></div>

        <div className="infrastructure-container">
          <div className="infrastructure-details">
            <div
              className="infrastructure-text-block"
              data-animate="fade-right"
            >
              <p>
                Flytutor is India's pioneer in drone training and AI-driven
                drone solutions, offering expert-led courses in aerial
                photography, hybrid drone technology, and advanced drone
                innovation.
                <br />
                <br />
                As a DGCA-authorized Remote Pilot Training Organisation (RPTO),
                we provide industry-grade equipment, expert instructors, modern
                labs, simulation centers, and a fully-equipped drone flying zone
                — everything you need to become a certified drone pilot in
                India.
              </p>
            </div>

            <div
              className="infrastructure-media-block"
              data-animate="fade-left"
            >
              <div className="infrastructure-video-wrapper">
                <video controls autoPlay muted loop>
                  <source src="fly_video.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Stats />

      {/* FACILITIES SECTION */}
      <section className="flytutor-facilities-showcase">
        <h2 className="flytutor-main-heading">Our Facilities</h2>

        <div className="facilities-card-spread">
          {[
            {
              img: "https://firebasestorage.googleapis.com/v0/b/aviatricks-4ffdf.firebasestorage.app/o/FLYTUTOR%2Ffacilities%2FIMG_6269.jpeg?alt=media&token=3dd78a45-0e2d-4441-bb6b-719eb42bcea8",
              title: "Modern Classrooms",
              desc: "Smart interactive learning environments.",
            },
            {
              img: "https://firebasestorage.googleapis.com/v0/b/aviatricks-4ffdf.firebasestorage.app/o/FLYTUTOR%2Ffacilities%2FInstructor.webp?alt=media&token=82ff3513-4c71-44ab-b73a-e158e2609733",
              title: "Expert Instructors",
              desc: "DGCA-certified trainers.",
            },
            {
              img: "https://firebasestorage.googleapis.com/v0/b/aviatricks-4ffdf.firebasestorage.app/o/FLYTUTOR%2Ffacilities%2Fcarrier_support.webp?alt=media&token=45d985b9-b3cb-4853-a65c-2ffa2fb3bca2",
              title: "Placement Support",
              desc: "Career-ready guidance & industry links.",
            },
            {
              img: "https://firebasestorage.googleapis.com/v0/b/aviatricks-4ffdf.firebasestorage.app/o/FLYTUTOR%2Ffacilities%2Fcommunity_hub.webp?alt=media&token=54e7b5d3-b956-457f-b49f-fab3e73d457c",
              title: "Community Space",
              desc: "Innovators hub & collaboration arena.",
            },
            {
              img: "https://firebasestorage.googleapis.com/v0/b/aviatricks-4ffdf.firebasestorage.app/o/FLYTUTOR%2Ffacilities%2FWhatsApp%20Image%202025-07-08%20at%208.25.28%20PM.jpeg?alt=media&token=96f36ab5-e238-4f7d-8f6a-580d16796201",
              title: "Drone Lab",
              desc: "Hands-on UAV innovation & testing zone.",
            },
            {
              img: "https://firebasestorage.googleapis.com/v0/b/aviatricks-4ffdf.firebasestorage.app/o/FLYTUTOR%2Ffacilities%2Flibrary.webp?alt=media&token=952ee014-c1b7-482f-af39-3b3d935319e8",
              title: "Drone Library",
              desc: "Drone research & manuals.",
            },
            {
              img: "https://firebasestorage.googleapis.com/v0/b/aviatricks-4ffdf.firebasestorage.app/o/FLYTUTOR%2Ffacilities%2Fgreenery_acre.webp?alt=media&token=6cfb3d67-c9d7-46a5-a5a0-074d0c3cf858",
              title: "24-acre Flying Zone",
              desc: "Massive open space for practice.",
            },
            {
              img: "https://firebasestorage.googleapis.com/v0/b/aviatricks-4ffdf.firebasestorage.app/o/FLYTUTOR%2Ffacilities%2Fhostel.webp?alt=media&token=8a07a46e-f4fa-4e43-9f89-43eaca6f30ad",
              title: "Premium Hostel",
              desc: "Comfortable AC student accommodation.",
            },
          ].map((item, i) => (
            <div className="facility-spread-card" key={i}>
              <img src={item.img} alt={item.title} />
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="flytutor-student-reviews">
        <h2 className="flytutor-main-heading">What Our Students Say</h2>

        <div className="student-reviews-container">
          {testimonials.map((t, i) => (
            <div className="student-review-item" key={t._id || i}>
              <p className="review-message">"{t.message}"</p>

              <div className="review-rating">
                {"★".repeat(t.rating)}
                {"☆".repeat(5 - t.rating)}
              </div>

              <h4 className="review-author">— {t.name}</h4>
            </div>
          ))}
        </div>
      </section>

      {/* ===== COLLABORATORS ===== */}

      <div className="flytutor-partners-carousel">
        <h2 className="flytutor-main-heading">Our Collaborations</h2>
        <div className="partners-carousel-track">
          {Array.isArray(collaborators) &&
            [...collaborators, ...collaborators].map((collaborator, index) => (
              <div key={index} className="partner-logo-wrapper">
                <div className="partner-logo-box">
                  <img
                    src={`${API_BASE_URL}${collaborator.url}`}
                    alt={collaborator.filename || "Partner Logo"}
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* ===== FRANCHISE CTA ===== */}
      <section className="flytutor-franchise-banner" data-animate="fade-up">
        <div className="franchise-banner-inner">
          <h2 className="franchise-banner-title">
            Become a Flytutor Franchise Partner
          </h2>

          <p className="franchise-banner-description">
            Join India's fastest-growing DGCA-approved drone training network
            and build a future-ready aviation business.
          </p>

          <button
            className="franchise-apply-button pulse-animation"
            onClick={() => navigate("/franchise")}
          >
            Apply for Franchise →
          </button>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <div className="flytutor-contact-section">
        <h2 className="collaboration-heading">Contact Us</h2>
        <p className="contact-section-subtitle">
          Get in touch with us for any queries or support.
        </p>
        <div className="contact-section-layout">
          <div className="contact-map-area">
            <iframe
              title="Flytutor Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3912.113137918836!2d78.0272093!3d11.326440500000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3babdbb297734803%3A0xd05a3e9e2582d7b4!2sFlytutor%20RPTO%20Training%20Cente!5e0!3m2!1sen!2sin!4v1756737038335!5m2!1sen!2sin"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          <div className="contact-form-area">
            <h3>Send a Message</h3>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <input
                type="number"
                name="number"
                placeholder="Phone Number"
                value={formData.number}
                onChange={handleChange}
                required
              />
              <textarea
                name="message"
                placeholder="Your message..."
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
              <button
                type="submit"
                className="contact-submit-button"
                disabled={contactLoading}
              >
                {contactLoading ? "Sending..." : "Send Message"}
              </button>
            </form>
            {status && <p className="contact-form-status">{status}</p>}
          </div>
        </div>
      </div>

      {/* ===== POPUPS ===== */}
      {showPopup && <Popup onClose={() => setShowPopup(false)} />}

      {showConsultModal && (
        <ConsultationModal onClose={() => setShowConsultModal(false)} />
      )}
    </div>
  );
};

export default Home;

// Slides data (add this if missing)
const slides = [
  {
    id: "68d3aff36059f48a7ca2d12f",
    img: "/hero1.webp",
    title: "SMALL RPC",
    description:
      "Small RPC , Big Opportunities -- Take control of the skies today!",
  },
  {
    id: "68d3b0a86059f48a7ca2d135",
    img: "/hero2.webp",
    title: "MEDIUM RPC",
    description:
      "Turn your passion into profession - Medium RPC is the next level of drone mastery!",
  },
  {
    id: "68d3b1316059f48a7ca2d138",
    img: "/Add on Course Images/hero3.webp",
    title: "SMALL + MEDIUM RPC",
    description:
      "Small RPC to start, Medium RPC to soar - Your drone journey begins here!",
  },
  {
    id: "68d3b2d96059f48a7ca2d153",
    img: "/Add on Course Images/hero4.webp",
    title: "FPV",
    description:
      "See the sky like never before -FPV flying puts you in the pilot's seat!",
  },
];